import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import config from '../config';
import './Chatbot.css';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import SourceCitation from './SourceCitation';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import { TableSkeleton } from './ui/TableSkeleton';
import { ThinkingIndicator } from './ui/ThinkingIndicator';
import { AgentActivityFeed } from './ui/AgentActivityFeed';
import { MermaidDiagram } from './ui/MermaidDiagram';
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker using unpkg CDN (mirrors npm packages directly)
// The version must match the installed pdfjs-dist package version
const PDFJS_VERSION = '5.4.449';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

// Context for passing citation click handler and sources to nested components
const CitationContext = createContext({ onCitationClick: null, sources: [], citationMap: {} });

// Citation link component - renders clickable superscript citations
const CitationLink = ({ citationIds }) => {
  const { onCitationClick, sources, citationMap } = useContext(CitationContext);
  const [tooltipPos, setTooltipPos] = useState(null);
  const badgeRef = useRef(null);

  // Parse citation IDs (handles "1", "1, 2", "1][2" formats)
  const ids = citationIds
    .split(/[,\]\[]+/)
    .map(id => parseInt(id.trim(), 10))
    .filter(id => !isNaN(id));

  if (ids.length === 0) return null;

  const handleClick = (e, id) => {
    e.preventDefault();
    if (onCitationClick) {
      onCitationClick(id);
    }
  };

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2
    });
  };

  const handleMouseLeave = () => {
    setTooltipPos(null);
  };

  const getTooltipData = (id) => {
    if (!citationMap || !sources) return null;
    const loc = citationMap[id];
    if (!loc) return null;
    const source = sources[loc.docIndex];
    if (!source) return null;
    const page = source.pages?.[loc.pageIndex];
    
    let authorsDisplay = source.authors;
    if (source.authors) {
       // Check if "et al" is already applied or if we need to shorten
       const authorList = source.authors.split(/[,;]|\s+and\s+/i).map(a => a.trim());
       if (authorList.length > 3 && !source.authors.includes('et al')) {
          authorsDisplay = `${authorList.slice(0, 3).join(', ')} et al.`;
       }
    }

    return {
      title: source.displayName,
      author: authorsDisplay,
      page: page?.pageNumber !== undefined ? page.pageNumber + 1 : null
    };
  };

  return (
    <span className="citation-group">
      {ids.map((id, idx) => {
        const info = getTooltipData(id);
        return (
          <span key={idx}>
            <span
              ref={badgeRef}
              className="citation-link"
              onClick={(e) => handleClick(e, id)}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              role="button"
              tabIndex={0}
            >
              {id}
            </span>
            {info && tooltipPos && (
              <div 
                className="
                  fixed w-max max-w-[300px] p-2
                  bg-gray-800 text-white text-xs rounded shadow-lg
                  z-[9999] pointer-events-none
                  text-left flex flex-col gap-0.5
                  font-sans font-normal leading-normal whitespace-normal
                  -translate-x-1/2 -translate-y-full
                "
                style={{
                  top: `${tooltipPos.top}px`,
                  left: `${tooltipPos.left}px`
                }}
              >
                <div className="font-semibold w-full break-words">{info.title}</div>
                {info.author && <div className="text-gray-300 w-full text-[10px] break-words">{info.author}</div>}
                {info.page && <div className="text-gray-400 text-[10px]">p. {info.page}</div>}
                
                {/* Arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </span>
        );
      })}
    </span>
  );
};

// Process text to convert citation markers [1], [2] to special format for react-markdown
const processCitationMarkers = (text) => {
  if (!text) return text;
  return text.replace(
    /\[(\d+(?:\s*,\s*\d+)*)\](?:\[(\d+(?:\s*,\s*\d+)*)\])*/g,
    (match) => {
      const numbers = match.match(/\d+/g);
      if (numbers && numbers.length > 0) {
        return `<cite data-citations="${numbers.join(',')}"></cite>`;
      }
      return match;
    }
  );
};

// Copy button component with feedback
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <button 
      className={`
        flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-all duration-200
        ${copied 
          ? 'text-emerald-500 bg-emerald-500/10 border border-emerald-500/20' 
          : 'text-kaleido-gray-500 bg-transparent border border-kaleido-purple/20 hover:text-kaleido-purple hover:bg-kaleido-purple/5 hover:border-kaleido-purple/40'}
      `}
      onClick={handleCopy}
      aria-label={copied ? 'Copied!' : 'Copy response'}
    >
      {copied ? (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Copied!</span>
        </>
      ) : (
        <>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2"/>
            <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5"/>
          </svg>
          <span>Copy</span>
        </>
      )}
    </button>
  );
};

// Interactive Author List Component (Internal)
// Expandable Text Component for long text like descriptions
const ExpandableText = ({ text, maxLength = 120 }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!text) return null;
  
  // If text is short enough, just show it
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }
  
  if (expanded) {
    return (
      <span>
        {text}
        <span 
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
          className="text-kaleido-purple cursor-pointer hover:underline ml-1 text-xs font-medium"
          role="button"
        >
          (less)
        </span>
      </span>
    );
  }
  
  // Show truncated text with expand option
  const truncatedText = text.substring(0, maxLength).trim();
  // Find the last space to avoid cutting words
  const lastSpace = truncatedText.lastIndexOf(' ');
  const displayText = lastSpace > maxLength * 0.7 ? truncatedText.substring(0, lastSpace) : truncatedText;
  
  return (
    <span>
      {displayText}
      <span className="text-kaleido-gray-400">...</span>
      <span 
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        className="text-kaleido-purple cursor-pointer hover:underline ml-1 text-xs font-medium"
        role="button"
      >
        (more)
      </span>
    </span>
  );
};

const AuthorList = ({ authorString }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!authorString) return null;

  // Extract full author list from HTML title attribute if present (backend sends HTML with title)
  let fullAuthorList = null;
  const titleMatch = authorString.match(/title="([^"]*)"/);
  if (titleMatch) {
    // Decode HTML entities in the title attribute
    fullAuthorList = titleMatch[1].replace(/&quot;/g, '"').replace(/&amp;/g, '&');
  }

  // Strip HTML tags to get plain text
  const stripHtml = (html) => html.replace(/<[^>]*>/g, '');
  const plainText = stripHtml(authorString);

  // Handle "et al. (+20)" case from backend
  const cleanAuthorString = plainText.replace(/\s*et al\.\s*\(\+\d+\)/, '');
  const hasBackendTruncation = plainText.includes('et al.');

  // Use full author list from title if available, otherwise parse from visible text
  const authorsSource = fullAuthorList || cleanAuthorString;
  const authors = authorsSource.split(/[,;]|\s+and\s+/i).map(author => author.trim()).filter(Boolean);
  
  // Format authors
  const formattedAuthors = authors.map(author => {
    // Basic cleaning, remove parentheses like (Atkins)
    return author.replace(/\s*\([^)]*\)/g, '');
  });

  // If fewer than 4 authors and no truncation, just show them all
  if (formattedAuthors.length < 4 && !hasBackendTruncation) {
    return <span>{formattedAuthors.join(', ')}</span>;
  }

  if (expanded) {
    return (
      <span>
        {formattedAuthors.join(', ')}
        <span 
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
          className="text-kaleido-purple cursor-pointer hover:underline ml-1 text-xs"
          role="button"
        >
          (show less)
        </span>
      </span>
    );
  }

  const visibleCount = 3;
  const visibleAuthors = formattedAuthors.slice(0, visibleCount).join(', ');
  const hiddenAuthors = formattedAuthors.slice(visibleCount);
  
  // Determine what to show in the tooltip
  let tooltipText = "Show all authors";
  if (hiddenAuthors.length > 0) {
    tooltipText = hiddenAuthors.join(', ');
  }

  return (
    <span>
      {visibleAuthors}
      <span>, </span>
      <span 
        className="
          inline-flex items-center justify-center
          bg-kaleido-purple/10 text-kaleido-purple 
          px-1.5 py-px rounded-md text-[10px] font-semibold tracking-wide
          cursor-pointer transition-colors duration-150
          hover:bg-kaleido-purple/20
          relative group/etal align-baseline ml-0.5 -mb-px
        "
        onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
      >
        <span className="italic">et al.</span>
        {/* Custom Tooltip */}
        <div className="
          absolute bottom-full right-0 mb-2
          hidden group-hover/etal:block
          bg-gray-800 text-white text-xs rounded px-3 py-2
          w-max max-w-xs z-50 shadow-lg pointer-events-none
          leading-relaxed text-left whitespace-normal
        ">
          {tooltipText}
          {/* Arrow */}
          <div className="absolute top-full right-2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
        </div>
      </span>
    </span>
  );
};

// Table Cell Component to handle citations
const TableCell = ({ content }) => {
  if (!content) return null;
  
  // Strip HTML tags first
  const stripHtml = (html) => html.replace(/<[^>]*>/g, '');
  const textContent = stripHtml(content);
  
  // Check if there are citations [1], [2], etc.
  const hasCitations = /\[(\d+(?:\s*,\s*\d+)*)\]/.test(textContent);
  
  if (!hasCitations) {
    // No citations, just render the HTML content
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  
  // Parse text and citations
  const parts = [];
  let lastIndex = 0;
  const regex = /\[(\d+(?:\s*,\s*\d+)*)\](?:\[(\d+(?:\s*,\s*\d+)*)\])*/g;
  let match;
  
  while ((match = regex.exec(textContent)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: textContent.substring(lastIndex, match.index)
      });
    }
    
    // Add citation
    const numbers = match[0].match(/\d+/g);
    if (numbers && numbers.length > 0) {
      parts.push({
        type: 'citation',
        ids: numbers.join(',')
      });
    }
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text
  if (lastIndex < textContent.length) {
    parts.push({
      type: 'text',
      content: textContent.substring(lastIndex)
    });
  }
  
  return (
    <span>
      {parts.map((part, idx) => {
        if (part.type === 'citation') {
          return <CitationLink key={idx} citationIds={part.ids} />;
        }
        return <span key={idx}>{part.content}</span>;
      })}
    </span>
  );
};

// Custom Table Component to handle JSON data
const JsonTable = ({ data }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyTable = async () => {
    try {
      const tableData = typeof data === 'string' ? JSON.parse(data) : data;
      const headers = tableData.headers || [];
      const rows = tableData.rows || [];
      
      // Strip HTML tags from cells
      const stripHtml = (html) => html.replace(/<[^>]*>/g, '');
      
      // Create HTML table
      let html = '<table border="1" cellpadding="4" cellspacing="0" style="border-collapse: collapse;">';
      html += '<thead><tr>';
      headers.forEach(header => {
        html += `<th style="background-color: #f3e8f7; color: #263A95; font-weight: 600; padding: 8px; text-align: left;">${stripHtml(header)}</th>`;
      });
      html += '</tr></thead><tbody>';
      rows.forEach(row => {
        html += '<tr>';
        row.forEach(cell => {
          html += `<td style="padding: 8px; border: 1px solid #ddd;">${stripHtml(cell)}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      
      // Create plain text fallback (tab-delimited)
      let text = headers.join('\t') + '\n';
      rows.forEach(row => {
        text += row.map(cell => stripHtml(cell)).join('\t') + '\n';
      });
      
      // Copy both HTML and plain text to clipboard
      const blob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([text], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': blob,
          'text/plain': textBlob
        })
      ]);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy table:', err);
    }
  };

  try {
    const tableData = typeof data === 'string' ? JSON.parse(data) : data;
    if (!tableData) return null;

    const headers = tableData.headers || [];
    const rows = tableData.rows || [];
    
    // Find Author column index
    const authorColIndex = headers.findIndex(h => h.toLowerCase().includes('author'));
    // Find Filename column index
    const filenameColIndex = headers.findIndex(h => h.toLowerCase().includes('filename'));
    // Find Description column index
    const descriptionColIndex = headers.findIndex(h => h.toLowerCase().includes('description'));

    return (
      <div className="w-full mb-4 relative group">
        <div className="flex items-center justify-between mb-2">
          {tableData.caption && <div className="font-bold">{tableData.caption}</div>}
          <button
            onClick={handleCopyTable}
            className={`
              flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-all duration-200
              ${copied 
                ? 'text-emerald-500 bg-emerald-500/10 border border-emerald-500/20' 
                : 'text-kaleido-gray-500 bg-transparent border border-kaleido-purple/20 hover:text-kaleido-purple hover:bg-kaleido-purple/5 hover:border-kaleido-purple/40'}
              ${tableData.caption ? '' : 'absolute -top-2 right-0'}
            `}
            aria-label="Copy table"
          >
            {copied ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5"/>
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
        {/* Scrollable table container with max-height so horizontal scrollbar is always visible */}
        <div 
          className="border border-gray-200 rounded-lg scrollbar-thin scrollbar-thumb-kaleido-purple/20 scrollbar-track-transparent bg-white"
          style={{ maxHeight: '70vh', overflow: 'auto' }}
        >
          <table 
            className="min-w-full text-sm text-left" 
            style={{ borderCollapse: 'separate', borderSpacing: 0, margin: 0, overflow: 'visible' }}
          >
            <thead>
              <tr style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                {headers.map((header, i) => (
                  <th 
                    key={i} 
                    className={`px-4 py-2 font-semibold text-kaleido-purple border-r border-kaleido-purple/20 last:border-r-0 ${
                      i === filenameColIndex ? 'max-w-[250px]' : ''
                    }`}
                    style={{ 
                      position: 'sticky',
                      top: 0,
                      zIndex: 50,
                      backgroundColor: 'rgb(243, 232, 247)', // kaleido-purple/10
                      boxShadow: '0 1px 0 rgba(152, 96, 167, 0.2)',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50 bg-white">
                  {row.map((cell, cellIndex) => (
                    <td 
                      key={cellIndex} 
                      className={`px-4 py-2 border-b border-r border-gray-100 last:border-r-0 align-top ${
                        cellIndex === filenameColIndex ? 'max-w-[250px] break-words' : ''
                      } ${
                        cellIndex === descriptionColIndex ? 'max-w-[350px]' : ''
                      }`}
                    >
                      {cellIndex === authorColIndex ? (
                        <AuthorList authorString={cell} />
                      ) : cellIndex === descriptionColIndex ? (
                        <ExpandableText text={cell} maxLength={120} />
                      ) : (
                        <TableCell content={cell} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {tableData.notes && <div className="text-xs text-gray-500 mt-2 italic">* {tableData.notes}</div>}
      </div>
    );
  } catch (e) {
    console.error('Error rendering JsonTable', e);
    return <div className="text-red-500 text-xs">Error loading table</div>;
  }
};

// Helper function to convert TABLE_JSON markers to HTML tables
const convertTableJsonToMarkdown = (jsonStr) => {
  // This function is kept for fallback but we will try to use the component
  return null; 
};

const preprocessMarkdown = (text) => {
  if (!text) return text;
  let processed = text;
  
  // Fix: Join numbered items (e.g., "1)\n\n**Text" or "1)\n**Text") that got split across lines
  // This handles cases where LLM outputs "1)" on its own line followed by the content
  processed = processed.replace(/(\d+\))\s*\n+\s*(\*\*[^*]+\*\*)/g, '$1 $2');
  
  // Also handle cases where the number is followed by just text (not bold)
  processed = processed.replace(/^(\d+\))\s*\n+(\s*)([A-Z])/gm, '$1 $3');
  
  // Fix: Convert "1)" lists to "1." lists so markdown recognizes them
  processed = processed.replace(/(^|\n)(\d+)\)([ \t])/g, '$1$2.$3');

  // Fix: Handle Bold ending (**...**) followed by inline list item
  // Only match if there is NO newline between bold and list marker to avoid breaking existing indentation
  processed = processed.replace(/([^\n])(\*\*)([ \t]*)(\d+\.\s|[*-]\s)/g, '$1$2\n\n$4');

  // Fix: Handle Punctuation (., :, )) followed by inline list item
  // Only match if there is NO newline between punctuation and list marker
  // Use negative lookbehind to EXCLUDE decimal numbering like 1.1., 2.3. (don't break headers like "#### 1.1. Title")
  processed = processed.replace(/(?<!\d)([.:)])([ \t]*)(\d+\.\s|[*-]\s)/g, '$1\n\n$3');

  // Fix: ensure proper line breaks BEFORE headers
  // Exclude '#' from matching the character before to avoid splitting "### Header" into "#" and "## Header"
  processed = processed.replace(/([^\n#])(#{1,6}\s+)/g, '$1\n\n$2');
  processed = processed.replace(/([^\n])\n(#{1,6}\s+)/g, '$1\n\n$2');
  // Fix: heading lines with body text stuck (e.g., "### HeadingIt is" -> "### Heading\n\nIt is")
  // Detects lowercase letter immediately followed by common sentence starters without space
  processed = processed.replace(/(#{1,6}\s+[^\n]*[a-z])(It |This |The |There |These |Those |A |An |In |On |At |For |If |As |While |When |Although |However |Therefore |Furthermore |Moreover |According |Based |What |Which |Who |Where |Why |How )/g, '$1\n\n$2');
  return processed;
};

const processTableMarkers = (text, isStreaming = false) => {
  if (!text) return text;
  const completePattern = /<!--TABLE_JSON(\{[\s\S]*?\})TABLE_JSON-->/g;
  let processed = text.replace(completePattern, (match, jsonStr) => {
    // Encode JSON for attribute
    try {
      // Sanitize JSON string by properly escaping control characters
      // that may have been included literally instead of escaped
      const sanitizedJson = jsonStr.replace(/[\x00-\x1F\x7F]/g, (char) => {
        const escapeMap = {
          '\n': '\\n', '\r': '\\r', '\t': '\\t', '\b': '\\b', '\f': '\\f'
        };
        return escapeMap[char] || '';
      });
      
      // Validate JSON first
      JSON.parse(sanitizedJson);
      // We use a custom element that we will map in markdownComponents
      // Use encodeURIComponent to be safe with attributes
      return `<json-table data="${encodeURIComponent(sanitizedJson)}"></json-table>`;
    } catch (e) {
      return match;
    }
  });
  if (isStreaming) {
    const incompleteMatch = processed.match(/<!--TABLE_JSON[\s\S]*$/);
    if (incompleteMatch) {
      const captionMatch = incompleteMatch[0].match(/"caption"\s*:\s*"([^"]+)"/);
      const caption = captionMatch ? captionMatch[1] : '';
      processed = processed.replace(/<!--TABLE_JSON[\s\S]*$/, `__TABLE_LOADING__${caption}__`);
    }
  }
  return processed;
};

// Attachment Preview Component
const AttachmentPreview = ({ attachment }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!attachment) return null;

  return (
    <div className="flex flex-col items-end mb-2 w-full">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-2 px-3 py-1.5 
          bg-white/80 backdrop-blur-sm border border-kaleido-purple/20 shadow-sm
          rounded-xl text-xs text-kaleido-purple font-medium
          hover:bg-white hover:shadow-md transition-all
          max-w-full
        "
      >
        <span className="truncate max-w-[200px]">📎 {attachment.name}</span>
        <span className="text-[10px] opacity-60 transform transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
        </span>
      </button>
      
      {isOpen && (
        <div className="
           mt-2 w-full max-w-[85%] lg:max-w-[70%] p-3 
           bg-white border border-kaleido-purple/10 rounded-xl 
           text-xs text-gray-600 font-mono whitespace-pre-wrap 
           max-h-[300px] overflow-y-auto shadow-inner
           scrollbar-thin scrollbar-thumb-gray-200
        ">
           {attachment.content}
        </div>
      )}
    </div>
  );
};

// Memoized message component
const MessageItem = React.memo(({ msg, isStreaming = false, citationMap = {}, onCitationClick }) => {
const sourceRefs = useRef({});
  const [expandToCitation, setExpandToCitation] = useState(null);

  const handleCitationClick = useCallback((citationId) => {
    setExpandToCitation(citationId);
    if (onCitationClick) {
      const location = citationMap[citationId];
      if (location) {
        onCitationClick(citationId, location.docIndex, location.pageIndex);
      }
    }
  }, [citationMap, onCitationClick]);

  const handleExpansionComplete = useCallback(() => {
    setExpandToCitation(null);
  }, []);

  const markdownComponents = {
    // Custom p component to prevent block elements (like json-table) from being wrapped in <p> tags
    // This fixes the "validateDOMNesting" React warning about <div>/<table> inside <p>
    p: ({ node, children, ...props }) => {
      // Check if any child is a json-table element (rendered as div)
      const hasBlockChild = React.Children.toArray(children).some(child => {
        if (React.isValidElement(child)) {
          // Check if it's a json-table component or any block element
          return child.type === 'json-table' || 
                 child.type === JsonTable ||
                 (typeof child.type === 'function' && child.type.name === 'JsonTable');
        }
        return false;
      });
      
      // If there's a block child, render children directly without <p> wrapper
      if (hasBlockChild) {
        return <>{children}</>;
      }
      
      // Otherwise, render as normal paragraph
      return <p {...props}>{children}</p>;
    },
    cite: ({ node, ...props }) => {
      const citations = props['data-citations'];
      if (citations) return <CitationLink citationIds={citations} />;
      return <cite {...props} />;
    },
    'json-table': ({ node, ...props }) => {
      const data = props.data ? decodeURIComponent(props.data) : null;
      return <JsonTable data={data} />;
    },
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match && match[1] === 'mermaid') {
        return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
      }
      return <code className={className} {...props}>{children}</code>;
    },
    table: ({ node, ...props }) => (
      <div className="w-full overflow-x-auto mb-4">
        <table className="min-w-full" {...props} />
      </div>
    )
  };

  if (msg.type === 'user') {
    return (
      <div className="flex flex-col items-end">
        {msg.attachment && <AttachmentPreview attachment={msg.attachment} />}
        <div className="
          bg-gradient-to-br from-kaleido-purple to-[#8050a0] 
          text-white py-[12px] px-[16px] rounded-2xl rounded-tr-sm shadow-md
          text-sm leading-relaxed max-w-[85%] lg:max-w-[70%]
          whitespace-pre-wrap break-words
        ">
          {msg.text}
        </div>
      </div>
    );
  } else if (msg.type === 'bot') {
    const preprocessed = preprocessMarkdown(msg.text);
    
    let processedText = processTableMarkers(preprocessed, isStreaming);
    processedText = processCitationMarkers(processedText);

    const renderContent = () => {
      // First, handle streaming table loading placeholders
      const loadingParts = processedText.split(/__TABLE_LOADING__([^_]*)__/);
      if (loadingParts.length > 1) {
        return loadingParts.map((part, index) => {
          if (index % 2 === 0) {
            if (!part || !part.trim()) return null;
            return (
              <Markdown
                key={index}
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeRaw, rehypeKatex]}
                components={markdownComponents}
              >
                {part}
              </Markdown>
            );
          } else {
            return <TableSkeleton key={index} caption={part || undefined} />;
          }
        });
      }
      
      // Handle completed tables: split at <json-table> elements and render them outside markdown
      // This prevents them from being wrapped in <p> tags which causes invalid DOM nesting
      const tablePattern = /<json-table\s+data="([^"]+)"><\/json-table>/g;
      const tableParts = processedText.split(tablePattern);
      
      if (tableParts.length === 1) {
        // No tables, render as normal markdown
        return (
          <Markdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeRaw, rehypeKatex]}
            components={markdownComponents}
          >
            {processedText}
          </Markdown>
        );
      }
      
      // Render parts: even indices are markdown text, odd indices are table data (encoded JSON)
      return tableParts.map((part, index) => {
        if (index % 2 === 0) {
          // Markdown text part
          if (!part || !part.trim()) return null;
          return (
            <Markdown
              key={`md-${index}`}
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={markdownComponents}
            >
              {part}
            </Markdown>
          );
        } else {
          // Table data part (encoded JSON) - render directly without markdown wrapper
          try {
            const tableData = decodeURIComponent(part);
            return <JsonTable key={`table-${index}`} data={tableData} />;
          } catch (e) {
            console.error('Error rendering table:', e);
            return null;
          }
        }
      });
    };

    return (
      <CitationContext.Provider value={{ onCitationClick: handleCitationClick, sources: msg.sources, citationMap: msg.citationMap }}>
        <div>
          <div className="bot-response-content text-sm">
            {renderContent()}
          </div>
          {msg.sources && msg.sources.length > 0 && (
            <div className="mt-4 pt-3 border-t border-kaleido-purple/20">
              <div className="text-xs font-bold text-kaleido-purple uppercase tracking-wider mb-2">Sources</div>
              {msg.sources.map((source, docIndex) => (
                <SourceCitation
                  key={docIndex}
                  source={source}
                  index={docIndex}
                  sourceRefs={sourceRefs}
                  expandToCitation={expandToCitation}
                  onExpansionComplete={handleExpansionComplete}
                />
              ))}
            </div>
          )}
          <div className="flex justify-end mt-2">
            <CopyButton text={msg.text} />
          </div>
        </div>
      </CitationContext.Provider>
    );
  } else {
    return null;
  }
});

// SVG Icon components for suggested prompts - modern duotone style
const PromptIcons = {
  layers: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" fillOpacity="0.15"/>
      <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M1 18l7.5-7.5 5 5L23 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17 6h6v6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="23" cy="6" r="2" fill="currentColor" fillOpacity="0.3"/>
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
      <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.6"/>
      <circle cx="12" cy="12" r="2.5" fill="currentColor"/>
      <path d="M12 2v4m0 12v4M2 12h4m12 0h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    </svg>
  ),
  calculator: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <rect x="4" y="2" width="16" height="20" rx="2.5" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2"/>
      <rect x="7" y="5" width="10" height="4" rx="1" fill="currentColor" fillOpacity="0.3"/>
      <circle cx="8" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="16" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="8" cy="16" r="1.2" fill="currentColor"/>
      <circle cx="12" cy="16" r="1.2" fill="currentColor"/>
      <circle cx="16" cy="16" r="1.2" fill="currentColor"/>
      <circle cx="8" cy="19" r="1.2" fill="currentColor" fillOpacity="0.5"/>
      <circle cx="12" cy="19" r="1.2" fill="currentColor" fillOpacity="0.5"/>
      <circle cx="16" cy="19" r="1.2" fill="currentColor" fillOpacity="0.5"/>
    </svg>
  ),
  cone: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 2l8 18H4L12 2z" fill="currentColor" fillOpacity="0.1"/>
      <path d="M12 2l8 18H4L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 14h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
      <path d="M5.5 18h13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <circle cx="12" cy="8" r="1.5" fill="currentColor"/>
    </svg>
  ),
  flowChart: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <rect x="3" y="3" width="6" height="5" rx="1.5" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="15" y="3" width="6" height="5" rx="1.5" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="16" width="6" height="5" rx="1.5" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 8v4h6m6-4v4h-6m0 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  ),
  pieChart: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
      <path d="M12 3a9 9 0 019 9h-9V3z" fill="currentColor" fillOpacity="0.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="2" fill="currentColor"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 6v6l4.5 2.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  ),
  risk: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 3L3 20h18L12 3z" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 9v4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="1.2" fill="currentColor"/>
    </svg>
  ),
  compare: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <rect x="4" y="10" width="4" height="10" rx="1" fill="currentColor" fillOpacity="0.3"/>
      <rect x="10" y="4" width="4" height="16" rx="1" fill="currentColor" fillOpacity="0.5"/>
      <rect x="16" y="7" width="4" height="13" rx="1" fill="currentColor" fillOpacity="0.4"/>
      <path d="M6 10V8M12 4V2m6 5V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 2"/>
    </svg>
  ),
  document: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 13h8m-8 4h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
    </svg>
  ),
  grid: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" fillOpacity="0.4" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  scale: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 2v20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M4 7l4 10H4l4-10z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M20 7l-4 10h4l-4-10z" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="12" cy="5" r="2" fill="currentColor"/>
    </svg>
  ),
  lightbulb: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 3a6 6 0 016 6c0 2.22-1.21 4.16-3 5.2V17a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2.8c-1.79-1.04-3-2.98-3-5.2a6 6 0 016-6z" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 21h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M10 17v1m4-1v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <circle cx="12" cy="9" r="1.5" fill="currentColor"/>
    </svg>
  ),
  puzzle: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M6 6h5v3a2 2 0 104 0V6h3v5h-3a2 2 0 100 4h3v3h-5v-3a2 2 0 10-4 0v3H6v-5h3a2 2 0 100-4H6V6z" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 6v2m-6 4h2m10 0h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      <path d="M12 12l3.5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="12" cy="12" r="2" fill="currentColor"/>
    </svg>
  )
};

// Suggested prompts for new conversations (16 total, 4 shown randomly)
const SUGGESTED_PROMPTS = [
  {
    icon: 'layers',
    title: 'Compare estimate classes',
    prompt: 'What are the differences between the estimating classes 1 through 5?'
  },
  {
    icon: 'trendUp',
    title: 'Earned value management',
    prompt: 'What is earned value management and how is it used in project control?'
  },
  {
    icon: 'cone',
    title: 'Estimating cone',
    prompt: 'What is the estimating cone and why is it important for project planning?'
  },
  {
    icon: 'calculator',
    title: 'Cost estimating methods',
    prompt: 'Compare the different cost estimating methods and when to use each.'
  },
  {
    icon: 'target',
    title: 'Estimate accuracy',
    prompt: 'How do you measure and improve the accuracy of cost estimates?'
  },
  {
    icon: 'flowChart',
    title: 'WBS structure',
    prompt: 'How should a Work Breakdown Structure be organized for cost estimating?'
  },
  {
    icon: 'risk',
    title: 'Risk & contingency',
    prompt: 'How should risk and contingency be factored into cost estimates?'
  },
  {
    icon: 'clock',
    title: 'Schedule integration',
    prompt: 'How do cost estimates integrate with project schedules?'
  },
  {
    icon: 'compare',
    title: 'Parametric vs analogous',
    prompt: 'When should I use parametric estimating versus analogous estimating?'
  },
  {
    icon: 'pieChart',
    title: 'Cost breakdown',
    prompt: 'What are the key elements of a cost breakdown structure?'
  },
  {
    icon: 'document',
    title: 'Estimate documentation',
    prompt: 'What documentation should accompany a cost estimate?'
  },
  {
    icon: 'gauge',
    title: 'Performance metrics',
    prompt: 'What are the key performance metrics for tracking project costs?'
  },
  {
    icon: 'lightbulb',
    title: 'Best practices',
    prompt: 'What are the industry best practices for developing credible cost estimates?'
  },
  {
    icon: 'scale',
    title: 'Estimate validation',
    prompt: 'How do you validate and cross-check a cost estimate?'
  },
  {
    icon: 'grid',
    title: 'Life cycle costs',
    prompt: 'What should be included in a life cycle cost estimate?'
  },
  {
    icon: 'puzzle',
    title: 'Uncertainty analysis',
    prompt: 'How do you perform uncertainty analysis on cost estimates?'
  }
];

// Function to get random prompts
const getRandomPrompts = (count = 4) => {
  const shuffled = [...SUGGESTED_PROMPTS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// Available prompt sets (personas)
const PROMPT_SETS = [
  { id: 'default', name: 'BSI Standards', description: 'Estimating and project controls standards', icon: '📐' },
  { id: 'aace', name: 'AACE International', description: 'Cost engineering and recommended practices', icon: '📊' },
  { id: 'di', name: 'Decision Intelligence', description: 'DI frameworks for PM and consultancy', icon: '🧠' },
];

// Available Gemini models for consolidation
const CONSOLIDATION_MODELS = [
  { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro', description: 'Most comprehensive, best reasoning (preview)', tier: 'premium' },
  { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash', description: 'Balanced speed and quality (preview)', tier: 'balanced' },
  { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash-Lite', description: 'Fastest, most economical', tier: 'fast' },
];

function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedContext, setUploadedContext] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [pdfThumbnail, setPdfThumbnail] = useState(null);
  const [displayedPrompts, setDisplayedPrompts] = useState(() => getRandomPrompts(4));
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 601);
  const [isShuffling, setIsShuffling] = useState(false);
  const { user, getAuthHeaders, isAuthenticated } = useAuth();

  const [shuffleKey, setShuffleKey] = useState(0);
  const [showParticles, setShowParticles] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gemini-3-flash-preview');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showReasoningSelector, setShowReasoningSelector] = useState(false);
  const [selectedPromptSet, setSelectedPromptSet] = useState(user?.prompt_config || 'default');
  const [showPromptSelector, setShowPromptSelector] = useState(false);
  const [customPromptSets, setCustomPromptSets] = useState([]);
  const [reasoningMode, setReasoningMode] = useState('standard'); // 'standard', 'advanced'
  const [activityStages, setActivityStages] = useState([]); // [{node, label, status, timestamp, data, id}]
  const [activityComplete, setActivityComplete] = useState(false);
  const activityStagesRef = useRef([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const restoringScrollRef = useRef(false);
  const particleCanvasRef = useRef(null);
  const promptContainerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const { currentSessionId, createSession, getSessionMessages, sessions, applySessionUpdate } = useSession();
  const modelSelectorRef = useRef(null);
  const reasoningSelectorRef = useRef(null);
  const promptSelectorRef = useRef(null);
  const pendingTableDataRef = useRef(null); // Store table data to add after streaming completes

  // Close model/prompt selectors when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelSelectorRef.current && !modelSelectorRef.current.contains(event.target)) {
        setShowModelSelector(false);
      }
      if (reasoningSelectorRef.current && !reasoningSelectorRef.current.contains(event.target)) {
        setShowReasoningSelector(false);
      }
      if (promptSelectorRef.current && !promptSelectorRef.current.contains(event.target)) {
        setShowPromptSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch custom prompt sets
  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${config.API_BASE_URL}/user/prompt-sets/`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      })
        .then(res => res.json())
        .then(data => {
          if (data.prompt_sets) {
            setCustomPromptSets(data.prompt_sets);
          }
        })
        .catch(() => {});
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 601);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Particle Network Animation - particles converge to card positions
  const runParticleAnimation = useCallback(() => {
    const canvas = particleCanvasRef.current;
    const container = promptContainerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Calculate card target positions (where particles will converge)
    const cardCount = isMobile ? 2 : 4;
    const cardTargets = [];
    const gap = isMobile ? 8 : 10;
    const cardHeight = isMobile ? 70 : 80;
    
    if (isMobile) {
      // Vertical stack: 2 cards
      const totalHeight = cardCount * cardHeight + (cardCount - 1) * gap;
      const startY = (rect.height - totalHeight) / 2;
      for (let i = 0; i < cardCount; i++) {
        cardTargets.push({
          x: rect.width / 2,
          y: startY + i * (cardHeight + gap) + cardHeight / 2,
          width: rect.width - 20,
          height: cardHeight,
        });
      }
    } else {
      // 2x2 grid
      const cardWidth = (rect.width - gap) / 2;
      const totalHeight = 2 * cardHeight + gap;
      const startY = (rect.height - totalHeight) / 2;
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 2; col++) {
          cardTargets.push({
            x: col * (cardWidth + gap) + cardWidth / 2,
            y: startY + row * (cardHeight + gap) + cardHeight / 2,
            width: cardWidth,
            height: cardHeight,
          });
        }
      }
    }
    
    // Kaleido brand colors
    const colors = ['#9860A7', '#263A95', '#b87fc4', '#4a5fc7'];
    
    // Create particles - distributed across card positions
    const particlesPerCard = isMobile ? 12 : 10;
    const particles = [];
    
    cardTargets.forEach((card, cardIndex) => {
      for (let i = 0; i < particlesPerCard; i++) {
        const angle = (Math.PI * 2 * i) / particlesPerCard + Math.random() * 0.3;
        const speed = 2 + Math.random() * 2.5; // Slower initial burst
        // Start from card center
        const startX = card.x + (Math.random() - 0.5) * card.width * 0.5;
        const startY = card.y + (Math.random() - 0.5) * card.height * 0.5;
        
        particles.push({
          x: startX,
          y: startY,
          startX,
          startY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          radius: 2 + Math.random() * 2.5,
          color: colors[Math.floor(Math.random() * colors.length)],
          alpha: 1,
          cardIndex,
          targetX: card.x + (Math.random() - 0.5) * card.width * 0.7,
          targetY: card.y + (Math.random() - 0.5) * card.height * 0.6,
          swirlAngle: angle,
          swirlRadius: 0,
          swirlCenterX: centerX,
          swirlCenterY: centerY,
          trail: [], // Array for trail positions
        });
      }
    });

    let frame = 0;
    const totalFrames = 150; // ~2.5 seconds at 60fps
    const expandFrames = 45;
    const swirlFrames = 55;
    const convergeFrames = 50;

    const animate = () => {
      ctx.clearRect(0, 0, rect.width, rect.height);
      frame++;

      const phase = frame < expandFrames ? 'expand' : 
                    frame < expandFrames + swirlFrames ? 'swirl' : 'converge';

      particles.forEach((p, i) => {
        if (phase === 'expand') {
          // Expand outward from card positions - slower, more graceful
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.97; // Slower deceleration
          p.vy *= 0.97;
          p.swirlRadius = Math.sqrt((p.x - p.swirlCenterX) ** 2 + (p.y - p.swirlCenterY) ** 2);
          p.swirlAngle = Math.atan2(p.y - p.swirlCenterY, p.x - p.swirlCenterX);
        } else if (phase === 'swirl') {
          // Swirl around center - slower, more elegant rotation
          const swirlProgress = (frame - expandFrames) / swirlFrames;
          p.swirlAngle += (0.035 + (i % 4) * 0.008) * p.speedMultiplier; // Slower swirl
          const targetRadius = p.swirlRadius * (1 - swirlProgress * 0.35);
          p.x = p.swirlCenterX + Math.cos(p.swirlAngle) * targetRadius;
          p.y = p.swirlCenterY + Math.sin(p.swirlAngle) * targetRadius;
        } else {
          // Converge to NEW card positions - smooth and graceful
          const convergeProgress = (frame - expandFrames - swirlFrames) / convergeFrames;
          const easeProgress = convergeProgress < 0.5 
            ? 4 * convergeProgress * convergeProgress * convergeProgress
            : 1 - Math.pow(-2 * convergeProgress + 2, 3) / 2; // ease in-out cubic
          
          p.x = p.x + (p.targetX - p.x) * (0.04 + easeProgress * 0.08) * p.speedMultiplier; // Slower convergence
          p.y = p.y + (p.targetY - p.y) * (0.04 + easeProgress * 0.08) * p.speedMultiplier;
          
          // Fade out as they reach destination
          if (convergeProgress > 0.7) {
            p.alpha = 1 - ((convergeProgress - 0.7) / 0.3);
          }
        }

        // Draw connecting lines (network effect)
        particles.forEach((p2, j) => {
          if (j <= i) return;
          const dx = p2.x - p.x;
          const dy = p2.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          // Stronger connections during swirl, card-grouped during converge
          let maxDist = isMobile ? 50 : 70;
          if (phase === 'converge' && p.cardIndex === p2.cardIndex) {
            maxDist = isMobile ? 80 : 100; // Stronger within same card
          }
          
          if (dist < maxDist) {
            const lineAlpha = (1 - dist / maxDist) * 0.5 * p.alpha * p2.alpha;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(152, 96, 167, ${lineAlpha})`;
            ctx.lineWidth = phase === 'converge' ? 1.5 : 1;
            ctx.stroke();
          }
        });

        // Update trail - keep last 5 positions
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 5) p.trail.shift();

        // Draw trails BEFORE particle (so particle is on top)
        for (let k = 0; k < p.trail.length; k++) {
          const trailAlpha = (k + 1) / p.trail.length * p.alpha * 0.3;
          const trailRadius = p.radius * (k + 1) / p.trail.length;
          
          ctx.beginPath();
          ctx.arc(p.trail[k].x, p.trail[k].y, trailRadius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(152, 96, 167, ${trailAlpha})`;
          ctx.fill();
        }

        // Draw particle
        const hex = p.color;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${p.alpha})`;
        ctx.fill();

        // Glow effect - stronger during converge
        const glowSize = phase === 'converge' ? p.radius * 3 : p.radius * 2;
        const glowAlpha = phase === 'converge' ? p.alpha * 0.3 : p.alpha * 0.15;
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${glowAlpha})`;
        ctx.fill();
      });

      if (frame < totalFrames) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Clear canvas when animation completes
        ctx.clearRect(0, 0, rect.width, rect.height);
      }
    };

    animate();
  }, [isMobile]);

  // Shuffle prompts with particle animation
  const handleShufflePrompts = useCallback(() => {
    if (isShuffling) return;
    
    setIsShuffling(true);
    setShowParticles(true);
    
    // Start particle animation immediately
    setTimeout(() => {
      runParticleAnimation();
    }, 50);
    
    // Change prompts while cards are fading out (much slower timing)
    setTimeout(() => {
      setDisplayedPrompts(getRandomPrompts(4));
      setShuffleKey(prev => prev + 1);
    }, 600); 
    
    // End shuffling state to let cards materialize (much slower, more graceful)
    // Cards need more time after state change before appearing
    setTimeout(() => {
      setIsShuffling(false);
    }, 1200);
    
    // End particle animation later for smoother, longer transition
    setTimeout(() => {
      setShowParticles(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }, 2800);
  }, [isShuffling, runParticleAnimation]);

  // File handling functions...
  const generatePdfThumbnail = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      
      const viewport = page.getViewport({ scale: 0.5 }); // Scale down for thumbnail
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      return canvas.toDataURL();
    } catch (error) {
      console.error('Error generating PDF thumbnail:', error);
      return null;
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    setPdfThumbnail(null); // Reset thumbnail

    // Generate thumbnail for PDFs
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      generatePdfThumbnail(file).then(thumbnail => {
        if (thumbnail) setPdfThumbnail(thumbnail);
      });
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      const authHeaders = getAuthHeaders();
      const headers = { ...authHeaders };
      delete headers['Content-Type'];
      const response = await fetch(`${config.API_BASE_URL}/upload`, {
        method: 'POST', headers, body: formData
      });
      const data = await response.json();
      if (!response.ok) {
        // Display the specific error message from the backend
        const errorMessage = data.error || 'Upload failed. Please try again.';
        setMessages(prev => [...prev, { text: `⚠️ ${errorMessage}`, type: 'bot' }]);
        setPdfThumbnail(null);
        return;
      }
      setUploadedFile({ name: file.name });
      setUploadedContext(data.text);
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessages(prev => [...prev, { text: '⚠️ Error uploading file. Please check your connection and try again.', type: 'bot' }]);
      setPdfThumbnail(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    setUploadedContext('');
    setPdfThumbnail(null);
  };

  // Session logic...
  const loadSessionMessages = useCallback(async (sessionId) => {
    if (!sessionId || !isAuthenticated) return;
    setIsLoadingHistory(true);
    try {
      const sessionMessages = await getSessionMessages(sessionId);
      // Check if we have a saved scroll position (hot reload scenario)
      const savedScrollPos = sessionStorage.getItem(`scrollPos_${sessionId}`);
      if (savedScrollPos) {
        restoringScrollRef.current = true;
      }

      if (sessionMessages && sessionMessages.length > 0) {
        const formattedMessages = sessionMessages.map(msg => {
          if (msg.type === 'bot' && msg.sources && msg.sources.length > 0) {
            const { groupedSources, citationMap } = formatSources(msg.sources);
            return { ...msg, sources: groupedSources, citationMap };
          }
          return msg;
        });
        setMessages(formattedMessages);
        setConversationStarted(true);
      } else {
        setMessages([]);
        setConversationStarted(false);
      }

      // Restore scroll position after DOM updates
      if (savedScrollPos) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = parseInt(savedScrollPos, 10);
            }
            restoringScrollRef.current = false;
          });
        });
      }
    } catch (err) {
      console.error('Error loading session messages:', err);
      restoringScrollRef.current = false;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [isAuthenticated, getSessionMessages]);

  useEffect(() => {
    const initSession = async () => {
      if (isAuthenticated && !currentSessionId && sessions.length === 0) {
        await createSession();
      }
    };
    initSession();
  }, [isAuthenticated, currentSessionId, sessions.length, createSession]);

  useEffect(() => {
    if (currentSessionId) {
      loadSessionMessages(currentSessionId);
    } else {
      setMessages([]);
      setConversationStarted(false);
    }
    // Randomize prompts for each new/switched session
    setDisplayedPrompts(getRandomPrompts(4));
  }, [currentSessionId, loadSessionMessages]);

  // Scroll logic...
  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 150;
    return (container.scrollHeight - container.scrollTop - container.clientHeight) <= threshold;
  };

  const handleScroll = () => {
    shouldAutoScrollRef.current = isNearBottom();
    // Save scroll position for hot reload recovery (skip if we're restoring)
    if (!restoringScrollRef.current && messagesContainerRef.current && currentSessionId) {
      sessionStorage.setItem(`scrollPos_${currentSessionId}`, messagesContainerRef.current.scrollTop);
    }
  };
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const currentCount = messages.length;
    const isNewMessage = currentCount > prevMessageCountRef.current;
    // Skip auto-scroll if we're restoring scroll position (hot reload)
    if (isNewMessage && messagesEndRef.current && shouldAutoScrollRef.current && !restoringScrollRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
    prevMessageCountRef.current = currentCount;
  }, [messages.length]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      const maxLines = 15;
      const maxHeight = (lineHeight * maxLines) + paddingTop + paddingBottom;
      if (scrollHeight > maxHeight) {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    }
  }, [input]);

  const handleInput = (e) => setInput(e.target.value);

  // Chat logic...
  const getBotResponse = async (userInput) => {
    let sessionToUse = currentSessionId;
    if (!sessionToUse && isAuthenticated) {
      const newSession = await createSession();
      if (newSession) sessionToUse = newSession.id;
    }
    if (!sessionToUse) return;

    if (!conversationStarted) setConversationStarted(true);
    
    // Create message object with attachment if present
    const newMessage = { 
      text: userInput, 
      type: 'user',
      attachment: uploadedFile ? {
        name: uploadedFile.name,
        content: uploadedContext
      } : null
    };
    
    setMessages(prev => [...prev, newMessage]);
    setIsLoading(true);
    setActivityStages([]);
    activityStagesRef.current = [];
    setActivityComplete(false);
    pendingTableDataRef.current = null; // Clear any pending table data from previous messages

    const contextToSend = uploadedContext;
    setUploadedFile(null);
    setUploadedContext('');

    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${config.API_BASE_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream', ...authHeaders },
        body: JSON.stringify({ 
          msg: userInput, 
          session_id: sessionToUse, 
          context: contextToSend, 
          model: selectedModel, 
          reasoning_mode: reasoningMode,
          ...(user?.can_select_prompt && { prompt_config: selectedPromptSet }) 
        })
      });

      if (!response.body) return;
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      
      let buffer = '';
      let tokenBuffer = '';
      let tokenCount = 0;
      const BATCH_SIZE = 1;
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Finalize the message with any remaining token buffer AND pending table data
          const pendingTable = pendingTableDataRef.current;
          console.log('[DEBUG] Stream done. pendingTable:', pendingTable ? 'exists' : 'null');
          setMessages(prev => {
            const newMessages = [...prev];
            const last = newMessages[newMessages.length - 1];
            console.log('[DEBUG] In setMessages. last.type:', last?.type, 'last.text length:', last?.text?.length);
            if (last && last.type === 'bot') {
              let finalText = last.text + (tokenBuffer || '');
              console.log('[DEBUG] finalText length before table:', finalText.length);
              
              // Apply pending table data if any
              if (pendingTable) {
                const tableMarker = `<!--TABLE_JSON${JSON.stringify(pendingTable)}TABLE_JSON-->`;
                console.log('[DEBUG] Applying pending table data, marker length:', tableMarker.length);
                
                // Find where to insert the table (after "Below is a complete list" or at the end)
                const insertPoint = finalText.indexOf('Below is a complete list of all available documents:');
                console.log('[DEBUG] insertPoint:', insertPoint);
                if (insertPoint >= 0) {
                  const afterPhrase = finalText.indexOf('\n', insertPoint + 'Below is a complete list of all available documents:'.length);
                  console.log('[DEBUG] afterPhrase:', afterPhrase);
                  if (afterPhrase >= 0) {
                    const before = finalText.substring(0, afterPhrase + 1);
                    const after = finalText.substring(afterPhrase + 1);
                    finalText = before + '\n' + tableMarker + '\n' + after;
                  } else {
                    finalText = finalText + '\n\n' + tableMarker;
                  }
                } else {
                  finalText = finalText + '\n\n' + tableMarker;
                }
                console.log('[DEBUG] finalText length after table:', finalText.length);
                pendingTableDataRef.current = null; // Clear the pending data
              }
              
              newMessages[newMessages.length - 1] = { ...last, text: finalText };
              console.log('[DEBUG] Updated message text length:', newMessages[newMessages.length - 1].text.length);
            }
            return newMessages;
          });
          
          // Ensure activity stages are marked complete if stream ends
          if (activityStagesRef.current.length > 0) {
            setActivityStages(prev => prev.map(s => s.status !== 'done' ? { ...s, status: 'done', timestamp: s.timestamp || Date.now() } : s));
            setActivityComplete(true);
          }
          
          setIsLoading(false);
          break;
        }
        buffer += value;
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';
        
        for (const event of events) {
          const lines = event.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                if (data.type === 'token') {
                  // Mark all stages done on first response token
                  if (tokenCount === 0 && activityStagesRef.current.length > 0) {
                    setActivityStages(prev => prev.map(s => s.status !== 'done' ? { ...s, status: 'done', timestamp: s.timestamp || Date.now() } : s));
                    activityStagesRef.current = activityStagesRef.current.map(s => s.status !== 'done' ? { ...s, status: 'done' } : s);
                    setActivityComplete(true);
                  }
                  tokenCount++;
                  tokenBuffer += data.content;
                  if (tokenCount >= BATCH_SIZE) {
                    const chunk = tokenBuffer;
                    tokenBuffer = '';
                    tokenCount = 0;
                    setMessages(prev => {
                      const newMessages = [...prev];
                      const last = newMessages[newMessages.length - 1];
                      if (last && last.type === 'bot') {
                        newMessages[newMessages.length - 1] = { ...last, text: last.text + chunk };
                      } else {
                        newMessages.push({ text: chunk, type: 'bot' });
                      }
                      return newMessages;
                    });
                  }
                } else if (data.type === 'text_replace') {
                  // Replace the entire text of the last bot message
                  // Used for post-stream corrections (e.g. renumbering citations)
                  if (data.content) {
                    setMessages(prev => {
                      const newMessages = [...prev];
                      const lastBotIndex = newMessages.map((m, i) => ({ type: m.type, index: i }))
                        .reverse().find(m => m.type === 'bot')?.index;
                      
                      if (lastBotIndex !== undefined) {
                        newMessages[lastBotIndex] = { ...newMessages[lastBotIndex], text: data.content };
                      }
                      return newMessages;
                    });
                  }
                } else if (data.type === 'table_data') {
                   // Store table data in ref to apply after streaming completes (avoids race condition)
                   console.log('[DEBUG] Received table_data event, storing for later:', data.data?.rows?.length, 'rows');
                   // Sanitize table data to remove control characters that break JSON parsing
                   const sanitizeForJson = (obj) => {
                     if (typeof obj === 'string') {
                       // Replace control characters with escaped versions or remove them
                       return obj.replace(/[\x00-\x1F\x7F]/g, (char) => {
                         switch(char) {
                           case '\n': return '\\n';
                           case '\r': return '\\r';
                           case '\t': return '\\t';
                           default: return ''; // Remove other control characters
                         }
                       });
                     }
                     if (Array.isArray(obj)) {
                       return obj.map(sanitizeForJson);
                     }
                     if (obj && typeof obj === 'object') {
                       const result = {};
                       for (const key in obj) {
                         result[key] = sanitizeForJson(obj[key]);
                       }
                       return result;
                     }
                     return obj;
                   };
                   pendingTableDataRef.current = sanitizeForJson(data.data);
                } else if (data.type === 'plan' || data.type === 'update_plan') {
                  // Plan events: initialize or update activity stages
                  console.log(`[ACTIVITY] ${data.type}:`, data.content);
                  // Ensure there's a bot message to attach the feed to
                  setMessages(prev => {
                    const last = prev[prev.length - 1];
                    if (!last || last.type !== 'bot') {
                      return [...prev, { text: '', type: 'bot' }];
                    }
                    return prev;
                  });
                  // We don't pre-build stages from the plan — stages are created
                  // dynamically as chain-of-thought SSE events arrive.
                  // The plan/update_plan events are now informational only.
                } else if (data.type === 'plan_progress') {
                  // No-op: progress is tracked via chain-of-thought events below
                } else if (data.type === 'routing_info') {
                  const now = Date.now();
                  const initStages = (isAdvanced) => {
                    const stages = [
                      { node: 'router', label: 'Analysing query', status: 'done', timestamp: now, data: data.content, id: 'router-0' },
                      { node: 'research', label: 'Searching knowledge base', status: 'active', timestamp: now, data: null, id: 'research-0' },
                    ];
                    if (isAdvanced) {
                      stages.push({ node: 'semantic_evaluate', label: 'Evaluating results', status: 'pending', timestamp: null, data: null, id: 'eval-0' });
                    }
                    stages.push({ node: 'consolidate', label: 'Synthesising response', status: 'pending', timestamp: null, data: null, id: 'consolidate-0' });
                    return stages;
                  };
                  const stages = initStages(reasoningMode === 'advanced');
                  setActivityStages(stages);
                  activityStagesRef.current = stages;
                  setActivityComplete(false);
                  // Ensure there's a bot message to attach the feed to
                  setMessages(prev => {
                    const last = prev[prev.length - 1];
                    if (!last || last.type !== 'bot') {
                      return [...prev, { text: '', type: 'bot' }];
                    }
                    return prev;
                  });
                } else if (data.type === 'search_activity') {
                  setActivityStages(prev => {
                    const updated = prev.map(s => {
                      if (s.node === 'research' && s.status === 'active') return { ...s, status: 'done', data: data.content };
                      return s;
                    });
                    // Activate next pending stage
                    const nextPending = updated.findIndex(s => s.status === 'pending');
                    if (nextPending !== -1) updated[nextPending] = { ...updated[nextPending], status: 'active', timestamp: Date.now() };
                    activityStagesRef.current = updated;
                    return updated;
                  });
                } else if (data.type === 'evaluation') {
                  setActivityStages(prev => {
                    const updated = prev.map(s => {
                      if (s.node === 'semantic_evaluate' && s.status === 'active') return { ...s, status: 'done', data: data.content };
                      return s;
                    });
                    // If recovery needed, insert reformulation + new research + new eval before consolidate
                    if (data.content.recovery_strategy && data.content.recovery_strategy !== 'none') {
                      const consolidateIdx = updated.findIndex(s => s.node === 'consolidate');
                      const retryNum = data.content.retry_count || 1;
                      const newStages = [
                        { node: 'reformulate_plan', label: 'Refining search strategy', status: 'active', timestamp: Date.now(), data: null, id: `reform-${retryNum}` },
                        { node: 'research', label: 'Searching knowledge base', status: 'pending', timestamp: null, data: null, id: `research-${retryNum}` },
                        { node: 'semantic_evaluate', label: 'Evaluating results', status: 'pending', timestamp: null, data: null, id: `eval-${retryNum}` },
                      ];
                      if (consolidateIdx !== -1) {
                        updated.splice(consolidateIdx, 0, ...newStages);
                      } else {
                        updated.push(...newStages);
                      }
                    } else {
                      // No recovery — activate next pending
                      const nextPending = updated.findIndex(s => s.status === 'pending');
                      if (nextPending !== -1) updated[nextPending] = { ...updated[nextPending], status: 'active', timestamp: Date.now() };
                    }
                    activityStagesRef.current = updated;
                    return updated;
                  });
                } else if (data.type === 'reformulation') {
                  setActivityStages(prev => {
                    const updated = prev.map(s => {
                      if (s.node === 'reformulate_plan' && s.status === 'active') return { ...s, status: 'done', data: data.content };
                      return s;
                    });
                    // Activate next pending (should be the new research stage)
                    const nextPending = updated.findIndex(s => s.status === 'pending');
                    if (nextPending !== -1) updated[nextPending] = { ...updated[nextPending], status: 'active', timestamp: Date.now() };
                    activityStagesRef.current = updated;
                    return updated;
                  });
                } else if (data.type === 'sources') {
                  if (data.content && data.content.length > 0) {
                    setMessages(prev => {
                      const newMessages = [...prev];
                      const lastBotIndex = newMessages.map((m, i) => ({ type: m.type, index: i }))
                        .reverse().find(m => m.type === 'bot')?.index;
                      if (lastBotIndex !== undefined) {
                        const { groupedSources, citationMap } = formatSources(data.content);
                        newMessages[lastBotIndex] = { ...newMessages[lastBotIndex], sources: groupedSources, citationMap };
                      }
                      return newMessages;
                    });
                  }
                } else if (data.type === 'session_update') {
                  if (data.session_id && (data.title !== undefined)) {
                    applySessionUpdate(data.session_id, { title: data.title });
                  }
                } else if (data.type === 'error') {
                  console.error("Backend stream error:", data.content);
                  setMessages(prev => [...prev, { text: `Error: ${data.content}`, type: 'bot', isError: true }]);
                  setActivityComplete(true);
                  setIsLoading(false);
                }
              } catch (e) { console.error("SSE parse error", e); }
            }
          }
        }
      }
    } catch (error) {
      console.error("Chat error", error);
      setMessages(prev => [...prev, { text: 'Sorry, something went wrong.', type: 'bot' }]);
      setIsLoading(false);
    }
  };

  const formatSources = (sources) => {
    // ... simplified logic reuse from original ...
    if (!sources || sources.length === 0) return { groupedSources: [], citationMap: {} };
    // (Full logic assumed preserved or copied from original file read)
    // For brevity in this response, I'm assuming the formatSources function logic is identical to previous read
    // In real execution, I would copy the full function body.
    // RE-INSERTING FULL LOGIC TO BE SAFE:
    const enhancedSources = sources.map((source, originalIndex) => {
      const citationId = originalIndex + 1;
      if (typeof source === 'object' && source.metadata) {
        const metadata = source.metadata;
        let documentTitle = metadata.document_title;
        if (!documentTitle && metadata.source) {
          const filename = metadata.source.includes('/') ? metadata.source.split('/').pop() : metadata.source;
          documentTitle = filename.replace(/\.(pdf|PDF)$/, '');
        }
        documentTitle = documentTitle || 'Unknown Source';
        let authorsArray = [];
        if (metadata.authors) {
          try { authorsArray = typeof metadata.authors === 'string' ? JSON.parse(metadata.authors) : metadata.authors; } catch (e) { authorsArray = [metadata.authors]; }
        }
        const authorsDisplay = authorsArray.length > 0 ? authorsArray.slice(0, 2).join(' & ') + (authorsArray.length > 2 ? ' et al.' : '') : null;
        const citationParts = [];
        if (metadata.publishing_body) citationParts.push(metadata.publishing_body);
        if (metadata.publication_date) citationParts.push(metadata.publication_date);
        
        let pageNumbers = null;
        if (metadata.pages) {
          try {
            const parsedPages = typeof metadata.pages === 'string' ? JSON.parse(metadata.pages) : metadata.pages;
            if (Array.isArray(parsedPages) && parsedPages.length > 0) {
              pageNumbers = parsedPages;
            }
          } catch {}
        }
        // Use nullish coalescing so page=0 remains valid (0-indexed first page).
        return { citationId, displayName: documentTitle, page: metadata.page ?? null, pageNumbers, authors: authorsDisplay, citationSubtitle: citationParts.join(', ') || null, fullMetadata: metadata, content: source.page_content || metadata.full_page_text || '' };
      }
      // Legacy string fallback
      return { citationId, displayName: 'Unknown Source', content: typeof source === 'string' ? source : '' };
    });

    const groupedByDocument = {};
    enhancedSources.forEach(source => {
      const docKey = source.fullMetadata?.source || source.displayName;
      if (!groupedByDocument[docKey]) {
        groupedByDocument[docKey] = { ...source, pages: [], seenPageKeys: new Set() };
      }
      const pageKey = `page_${source.page}_${JSON.stringify(source.pageNumbers || [])}_${source.content?.slice(0, 50)}`;
      if (!groupedByDocument[docKey].seenPageKeys.has(pageKey)) {
        groupedByDocument[docKey].seenPageKeys.add(pageKey);
        groupedByDocument[docKey].pages.push({ citationId: source.citationId, pageNumber: source.page, pageNumbers: source.pageNumbers, content: source.content, allCitationIds: [source.citationId] });
      }
    });
    
    const groupedSources = Object.values(groupedByDocument).map(doc => {
      doc.pages.sort((a, b) => (a.pageNumber || 0) - (b.pageNumber || 0));
      delete doc.seenPageKeys;
      return doc;
    });

    const citationMap = {};
    groupedSources.forEach((doc, docIndex) => {
      doc.pages.forEach((page, pageIndex) => {
        if (page.citationId) citationMap[page.citationId] = { docIndex, pageIndex };
        if (page.allCitationIds) page.allCitationIds.forEach(id => citationMap[id] = { docIndex, pageIndex });
      });
    });
    return { groupedSources, citationMap };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && (input.trim() || uploadedContext) && !isLoading) {
      e.preventDefault();
      const userInput = input;
      setInput('');
      getBotResponse(userInput);
    }
  };

  const handleSubmit = () => {
    if ((input.trim() || uploadedContext) && !isLoading) {
      const userInput = input;
      setInput('');
      getBotResponse(userInput);
    }
  };

  const handleSuggestedPrompt = (prompt) => {
    if (!isLoading) {
      getBotResponse(prompt);
    }
  };

  // Get current model info for display
  const currentModel = CONSOLIDATION_MODELS.find(m => m.id === selectedModel) || CONSOLIDATION_MODELS[1];
  const allPromptSets = [
    ...PROMPT_SETS,
    ...customPromptSets.map(s => ({ id: `custom:${s.id}`, name: s.name, description: 'Custom', icon: '\uD83D\uDD27' })),
  ];
  const currentPromptSet = allPromptSets.find(p => p.id === selectedPromptSet) || PROMPT_SETS[0];
  const canSelectPrompt = user?.can_select_prompt;

  return (
    <div className="flex flex-col h-full w-full relative">

      {/* Mobile Model Selector - Rendered via portal to header */}
      {isMobile && typeof document !== 'undefined' && document.getElementById('mobile-model-selector') && createPortal(
        <div className="relative" ref={modelSelectorRef}>
          <button
            onClick={() => setShowModelSelector(!showModelSelector)}
            disabled={isLoading}
            className={`
              flex items-center justify-center w-[28px] h-[28px] rounded-full bg-transparent
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span className={`
              w-[28px] h-[28px] rounded-full shrink-0 shadow-sm
              ${currentModel.tier === 'premium' ? 'bg-amber-500' :
                currentModel.tier === 'balanced' ? 'bg-emerald-500' : 'bg-sky-500'}
            `}></span>
          </button>
          
          {/* Mobile Dropdown */}
          {showModelSelector && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-[9999]">
              {CONSOLIDATION_MODELS.map((model) => (
                <button
                  key={model.id}
                  onClick={() => {
                    setSelectedModel(model.id);
                    setShowModelSelector(false);
                  }}
                  className={`
                    w-full px-2 py-1.5 text-left hover:bg-kaleido-purple/5 transition-colors
                    ${selectedModel === model.id ? 'bg-kaleido-purple/10' : ''}
                  `}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`
                      w-1.5 h-1.5 rounded-full shrink-0
                      ${model.tier === 'premium' ? 'bg-amber-500' : 
                        model.tier === 'balanced' ? 'bg-emerald-500' : 'bg-sky-500'}
                    `}></span>
                    <span className="font-medium text-[11px] text-kaleido-gray-800">{model.name}</span>
                    {selectedModel === model.id && (
                      <svg className="w-2.5 h-2.5 text-kaleido-purple shrink-0 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>,
        document.getElementById('mobile-model-selector')
      )}

      {/* Mobile Reasoning Selector - Rendered via portal to header */}
      {isMobile && typeof document !== 'undefined' && document.getElementById('mobile-reasoning-selector') && createPortal(
        <div className="relative" ref={reasoningSelectorRef}>
          {(() => {
            const mobileReasoningModes = {
              standard: {
                color: 'bg-sky-500', textColor: 'text-sky-500',
                icon: <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
              },
              advanced: {
                color: 'bg-amber-500', textColor: 'text-amber-500',
                icon: <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
              },
            };
            const cur = mobileReasoningModes[reasoningMode] || mobileReasoningModes.standard;
            return (
              <>
                <button
                  onClick={() => setShowReasoningSelector(!showReasoningSelector)}
                  disabled={isLoading}
                  className={`
                    flex items-center justify-center w-[28px] h-[28px] rounded-full bg-transparent
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  title="Select reasoning mode"
                >
                  <span className={`
                    flex items-center justify-center w-[28px] h-[28px] rounded-full shadow-sm text-white
                    ${cur.color}
                  `}>
                    {cur.icon}
                  </span>
                </button>

                {showReasoningSelector && (
                  <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-[9999]">
                    {[
                      { id: 'standard', name: 'Standard', ...mobileReasoningModes.standard },
                      { id: 'advanced', name: 'Advanced', ...mobileReasoningModes.advanced }
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => {
                          setReasoningMode(mode.id);
                          setShowReasoningSelector(false);
                        }}
                        className={`
                          w-full px-2 py-1.5 text-left hover:bg-kaleido-purple/5 transition-colors
                          ${reasoningMode === mode.id ? 'bg-kaleido-purple/10' : ''}
                        `}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`${mode.textColor} shrink-0`}>{mode.icon}</span>
                          <span className="font-medium text-[11px] text-kaleido-gray-800">{mode.name}</span>
                          {reasoningMode === mode.id && (
                            <svg className={`w-2.5 h-2.5 ${mode.textColor} shrink-0 ml-auto`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>,
        document.getElementById('mobile-reasoning-selector')
      )}

      {/* Mobile Prompt Set Selector - Rendered via portal to header */}
      {isMobile && canSelectPrompt && typeof document !== 'undefined' && document.getElementById('mobile-prompt-selector') && createPortal(
        <div className="relative" ref={promptSelectorRef}>
          <button
            onClick={() => setShowPromptSelector(!showPromptSelector)}
            disabled={isLoading}
            className={`
              flex items-center justify-center w-[28px] h-[28px] rounded-full bg-transparent
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span className="
              flex items-center justify-center w-[28px] h-[28px] rounded-full
              bg-kaleido-purple/15 text-[14px] leading-none shadow-sm
            ">
              {currentPromptSet.icon}
            </span>
          </button>
          
          {showPromptSelector && (
            <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-[9999]">
              {allPromptSets.map((ps, idx) => (
                <React.Fragment key={ps.id}>
                  {idx === PROMPT_SETS.length && customPromptSets.length > 0 && (
                    <div className="border-t border-kaleido-gray-200 my-0.5" />
                  )}
                  <button
                    onClick={() => {
                      setSelectedPromptSet(ps.id);
                      setShowPromptSelector(false);
                    }}
                    className={`
                      w-full px-2 py-1.5 text-left hover:bg-kaleido-purple/5 transition-colors
                      ${selectedPromptSet === ps.id ? 'bg-kaleido-purple/10' : ''}
                    `}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs shrink-0">{ps.icon}</span>
                      <span className="font-medium text-[11px] text-kaleido-gray-800">{ps.name}</span>
                      {selectedPromptSet === ps.id && (
                        <svg className="w-2.5 h-2.5 text-kaleido-purple shrink-0 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>,
        document.getElementById('mobile-prompt-selector')
      )}

      {/* Messages Area */}
      <div
        className={`
          flex-1 overflow-y-auto overflow-x-hidden w-full flex flex-col
          scrollbar-thin scrollbar-thumb-kaleido-purple/20 scrollbar-track-transparent
          pt-[calc(4rem+env(safe-area-inset-top))] lg:pt-14
          ${conversationStarted ? 'pb-2' : ''}
        `}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {/* Desktop Glassmorphic Toolbar - rendered via portal to span full width of content area */}
        {!isMobile && typeof document !== 'undefined' && document.getElementById('desktop-toolbar-portal') && createPortal(
          <div className="flex items-center justify-end gap-1.5 px-3 py-1.5 bg-white/50 backdrop-blur-md border-b border-white/20 shadow-sm">
            {/* Prompt Persona Selector - icon only */}
            <div className="relative" ref={promptSelectorRef}>
              <button
                onClick={() => canSelectPrompt && setShowPromptSelector(!showPromptSelector)}
                disabled={isLoading}
                className={`
                  flex items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent
                  transition-all duration-200
                  ${isLoading || !canSelectPrompt ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                  title={`Prompt set: ${currentPromptSet.name}`}
                >
                  <span className="
                    flex items-center justify-center w-[31px] h-[31px] rounded-full text-sm
                    bg-white/70 backdrop-blur-sm border border-kaleido-purple/15 hover:border-kaleido-purple/35
                    shadow-sm
                  ">
                    {currentPromptSet.icon}
                  </span>
                </button>

                {canSelectPrompt && showPromptSelector && (
                  <div className="absolute top-full right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-50">
                    {allPromptSets.map((ps, idx) => (
                      <React.Fragment key={ps.id}>
                        {idx === PROMPT_SETS.length && customPromptSets.length > 0 && (
                          <div className="border-t border-kaleido-gray-200 my-0.5" />
                        )}
                        <button
                          onClick={() => {
                            setSelectedPromptSet(ps.id);
                            setShowPromptSelector(false);
                          }}
                          className={`
                            w-full px-2 py-1 text-left hover:bg-kaleido-purple/5 transition-colors
                            ${selectedPromptSet === ps.id ? 'bg-kaleido-purple/10' : ''}
                          `}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs shrink-0">{ps.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-xs text-kaleido-gray-800">{ps.name}</div>
                              <div className="text-xs text-kaleido-gray-400 leading-tight">{ps.description}</div>
                            </div>
                            {selectedPromptSet === ps.id && (
                              <svg className="w-3 h-3 text-kaleido-purple shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
            </div>

            {/* Model Selector - icon only */}
            <div className="relative" ref={modelSelectorRef}>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                disabled={isLoading}
                className={`
                  flex items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent
                  transition-all duration-200
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                title={`Model: ${currentModel.name}`}
              >
                <span className="flex items-center justify-center w-[31px] h-[31px] rounded-full bg-white/70 backdrop-blur-sm border border-kaleido-purple/15 hover:border-kaleido-purple/35 shadow-sm">
                  <span className={`
                    w-2.5 h-2.5 rounded-full
                    ${currentModel.tier === 'premium' ? 'bg-amber-500' :
                      currentModel.tier === 'balanced' ? 'bg-emerald-500' : 'bg-sky-500'}
                  `}></span>
                </span>
              </button>

              {showModelSelector && (
                <div className="absolute top-full right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-50">
                  {CONSOLIDATION_MODELS.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        setSelectedModel(model.id);
                        setShowModelSelector(false);
                      }}
                      className={`
                        w-full px-2 py-1 text-left hover:bg-kaleido-purple/5 transition-colors
                        ${selectedModel === model.id ? 'bg-kaleido-purple/10' : ''}
                      `}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`
                          w-1.5 h-1.5 rounded-full shrink-0
                          ${model.tier === 'premium' ? 'bg-amber-500' :
                            model.tier === 'balanced' ? 'bg-emerald-500' : 'bg-sky-500'}
                        `}></span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-xs text-kaleido-gray-800">{model.name}</div>
                          <div className="text-xs text-kaleido-gray-400 leading-tight">{model.description}</div>
                        </div>
                        {selectedModel === model.id && (
                          <svg className="w-3 h-3 text-kaleido-purple shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reasoning Mode Selector - icon only, colour-coded */}
            <div className="relative" ref={reasoningSelectorRef}>
              {(() => {
                const reasoningModes = {
                  standard: {
                    color: 'bg-sky-500',
                    borderColor: 'border-sky-500',
                    hoverBorder: 'hover:border-sky-400',
                    textColor: 'text-sky-500',
                    icon: (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ),
                  },
                  advanced: {
                    color: 'bg-amber-500',
                    borderColor: 'border-amber-500',
                    hoverBorder: 'hover:border-amber-400',
                    textColor: 'text-amber-500',
                    icon: (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    ),
                  },
                };
                const current = reasoningModes[reasoningMode] || reasoningModes.standard;
                return (
                  <>
                    <button
                      onClick={() => setShowReasoningSelector(!showReasoningSelector)}
                      disabled={isLoading}
                      className={`
                        flex items-center justify-center w-[31px] h-[31px] rounded-full bg-transparent
                        transition-all duration-200
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      title={`Reasoning: ${reasoningMode === 'standard' ? 'Standard' : 'Advanced'}`}
                    >
                      <span className={`
                        flex items-center justify-center w-[31px] h-[31px] rounded-full shadow-sm text-white
                        ${current.color}
                      `}>
                        {current.icon}
                      </span>
                    </button>

                    {showReasoningSelector && (
                      <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-kaleido-purple/20 py-0.5 z-50">
                        {[
                          { id: 'standard', name: 'Standard', desc: 'Fast, single-pass research', ...reasoningModes.standard },
                          { id: 'advanced', name: 'Advanced', desc: 'Multi-step planning & review', ...reasoningModes.advanced }
                        ].map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => {
                              setReasoningMode(mode.id);
                              setShowReasoningSelector(false);
                            }}
                            className={`
                              w-full px-2 py-1.5 text-left hover:bg-kaleido-purple/5 transition-colors
                              ${reasoningMode === mode.id ? 'bg-kaleido-purple/10' : ''}
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`${mode.textColor} shrink-0`}>{mode.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-xs text-kaleido-gray-800">{mode.name}</div>
                                <div className="text-[10px] text-kaleido-gray-400 leading-tight">{mode.desc}</div>
                              </div>
                              {reasoningMode === mode.id && (
                                <svg className={`w-3 h-3 ${mode.textColor} shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>,
          document.getElementById('desktop-toolbar-portal')
        )}

        {isLoadingHistory ? (
          <div className="flex flex-col items-center justify-center gap-2 text-kaleido-gray-500 text-sm">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-kaleido-purple animate-bounce"></div>
              <div className="w-2 h-2 rounded-full bg-kaleido-purple animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 rounded-full bg-kaleido-purple animate-bounce [animation-delay:-0.3s]"></div>
            </div>
            <span>Loading conversation...</span>
          </div>
        ) : !conversationStarted ? (
          /* Greeting + Suggested Prompts - Centered Together */
          <div className="flex flex-col items-center gap-16 text-center w-full mt-auto mb-auto pt-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-kaleido-purple to-kaleido-blue leading-tight px-4">
              Hi, I'm Kal.<br />
              How can I help?
            </h1>
            
            {/* Suggested Prompts - Original width, closer to greeting */}
            <div className="relative w-full max-w-full sm:max-w-[90%] lg:max-w-4xl px-3 sm:px-4" ref={promptContainerRef}>
              {/* Particle canvas overlay - behind cards */}
              <canvas
                ref={particleCanvasRef}
                className={`
                  absolute inset-0 pointer-events-none z-0
                  transition-opacity duration-300
                  ${showParticles ? 'opacity-100' : 'opacity-0'}
                `}
              />
              
              {/* Prompt cards */}
              <div className={`
                relative z-10
                flex flex-col gap-2
                sm:grid sm:grid-cols-2 sm:grid-rows-2 sm:auto-rows-fr sm:gap-2.5
                transition-all ease-out
                ${isShuffling ? 'opacity-0 scale-[0.95] rotate-[5deg] duration-1000' : 'opacity-100 scale-100 rotate-0 duration-0'}
              `}>
                {displayedPrompts.slice(0, isMobile ? 2 : 4).map((item, index) => (
                  <button
                    key={`${shuffleKey}-${index}`}
                    onClick={() => handleSuggestedPrompt(item.prompt)}
                    disabled={isLoading || isShuffling}
                    className={`
                      group relative flex items-center gap-2 p-2
                      sm:gap-2.5 sm:p-2.5
                      h-full
                      bg-white/60 hover:bg-white/90 backdrop-blur-sm
                      border border-kaleido-purple/10 hover:border-kaleido-purple/30
                      rounded-[20px] shadow-sm hover:shadow-md
                      text-left transition-all duration-200
                      hover:-translate-y-0.5
                      disabled:cursor-not-allowed disabled:hover:translate-y-0
                      ${!isShuffling ? 'animate-cardMaterialize' : 'opacity-0'}
                    `}
                  >
                    <span className="
                      flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center
                      bg-gradient-to-br from-kaleido-purple/20 via-kaleido-purple/10 to-kaleido-blue/20
                      group-hover:from-kaleido-purple/30 group-hover:via-kaleido-purple/15 group-hover:to-kaleido-blue/30
                      text-kaleido-purple group-hover:text-kaleido-purple
                      rounded-[14px] transition-all duration-300 ease-out
                      shadow-sm group-hover:shadow-md
                      group-hover:scale-105
                      ring-1 ring-kaleido-purple/10 group-hover:ring-kaleido-purple/25
                    ">
                      {PromptIcons[item.icon]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.08em] text-kaleido-gray-800 group-hover:text-kaleido-purple transition-colors">
                        {item.title}
                      </div>
                      <div className="text-[10px] sm:text-[11px] text-kaleido-gray-500 mt-0.5 leading-snug normal-case font-normal tracking-normal">
                        {item.prompt}
                      </div>
                    </div>
                    <svg 
                      className="
                        flex-shrink-0 w-3.5 h-3.5 text-kaleido-gray-300 
                        group-hover:text-kaleido-purple group-hover:translate-x-0.5
                        transition-all duration-200
                        hidden sm:block
                      " 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
              
              {/* Shuffle button */}
              <div className="relative z-10 flex items-center justify-center mt-3">
                <button
                  onClick={handleShufflePrompts}
                  disabled={isShuffling}
                  className={`
                    group inline-flex items-center gap-2 pl-3 pr-4 py-2
                    text-[11px] sm:text-xs font-medium tracking-wide
                    text-kaleido-gray-600 hover:text-kaleido-purple
                    bg-white/70 hover:bg-white
                    border border-kaleido-purple/15 hover:border-kaleido-purple/40
                    rounded-full shadow-sm hover:shadow
                    transition-all duration-200
                    disabled:cursor-not-allowed
                    ${isShuffling ? 'text-kaleido-purple border-kaleido-purple/40 bg-kaleido-purple/5' : ''}
                  `}
                  aria-label="Show different suggestions"
                >
                  <svg
                    className={`
                      w-4 h-4 transition-transform duration-500 ease-out
                      ${isShuffling ? 'animate-spin [animation-duration:1.6s]' : 'group-hover:scale-110 group-hover:rotate-[20deg]'}
                    `}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path
                      d="M13 3 L14.5 8.5 L20 10 L14.5 11.5 L13 17 L11.5 11.5 L6 10 L11.5 8.5 Z"
                      fill="currentColor"
                      fillOpacity="0.9"
                      stroke="none"
                    />
                    <path
                      d="M6 15 L6.7 17.3 L9 18 L6.7 18.7 L6 21 L5.3 18.7 L3 18 L5.3 17.3 Z"
                      fill="currentColor"
                      fillOpacity="0.55"
                      stroke="none"
                      className={isShuffling ? 'animate-pulse' : ''}
                    />
                  </svg>
                  <span>{isShuffling ? 'Discovering...' : 'More ideas'}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Conversation Messages */
          <>
            {messages.map((msg, index) => {
              const isLastMessage = index === messages.length - 1;
              const isStreamingThisMessage = isLoading && isLastMessage && msg.type === 'bot';
              // Show activity feed above the last bot message when stages are active
              const showActivity = activityStages.length > 0 && isLastMessage && msg.type === 'bot';
              return (
                <div key={index} className="w-full max-w-full sm:max-w-[90%] lg:max-w-4xl mx-auto px-3 sm:px-8 py-2">
                  {showActivity && (
                    <AgentActivityFeed
                      stages={activityStages}
                      isComplete={activityComplete}
                    />
                  )}
                  <MessageItem msg={msg} isStreaming={isStreamingThisMessage} citationMap={msg.citationMap || {}} />
                </div>
              );
            })}
            {isLoading && messages.length > 0 && messages[messages.length - 1].type !== 'bot' && (
              <div className="w-full max-w-full sm:max-w-[90%] lg:max-w-4xl mx-auto px-3 sm:px-8 py-2">
                <ThinkingIndicator />
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area - Sticky at bottom of main content */}
      <div className="shrink-0 w-full max-w-full sm:max-w-[90%] lg:max-w-[48rem] mx-auto px-3 sm:px-4 pb-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] sm:pb-[max(1.25rem,calc(env(safe-area-inset-bottom)+0.75rem))] pt-1.5 sm:pt-2">
        <div className="
          relative flex flex-col 
          bg-white/95 backdrop-blur-xl border border-white/80
          rounded-3xl sm:rounded-[2rem] shadow-[0_-4px_16px_rgba(152,96,167,0.15),0_2px_8px_rgba(38,58,149,0.08)]
          overflow-hidden transition-all duration-300
        ">
          
          {/* Main Input Section */}
          <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 relative z-10">
            {/* File Upload Button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.txt"
            />
            <button
              className="
                p-1 sm:p-1.5 text-kaleido-gray-500 hover:text-kaleido-gray-700 hover:bg-kaleido-gray-100
                rounded-full transition-all disabled:opacity-50 shrink-0
              "
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isUploading}
              title="Upload context document (PDF/Text)"
            >
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-kaleido-gray-300 border-t-kaleido-gray-700 rounded-full animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>

            {/* Text Input */}
            <textarea
              ref={textareaRef}
              rows="1"
              value={input}
              onChange={handleInput}
              onKeyPress={handleKeyPress}
              placeholder={isMobile ? "Ask Kal..." : "Type your message here..."}
              className="
                flex-1 bg-transparent border-none outline-none 
                text-sm text-kaleido-gray-900 placeholder:text-kaleido-gray-400 
                resize-none overflow-hidden max-h-[60vh] py-2 px-2 sm:py-[10px] sm:px-4 rounded-3xl
              "
            />

            {/* Send Button */}
            <button 
              onClick={handleSubmit}
              disabled={(!input.trim() && !uploadedContext) || isLoading}
              className="
                p-1 rounded-[20px]
                bg-gradient-to-br from-kaleido-purple to-kaleido-blue
                text-white shadow-md
                hover:-translate-y-px hover:shadow-lg hover:to-[#3648a5]
                active:translate-y-0 active:shadow-md
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
                transition-all duration-200
              "
              aria-label="Send message"
            >
              <svg className="-translate-x-[1px] translate-y-[1px]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Attachment Extension Area */}
          {(uploadedFile || isUploading) && (
            <div className="
              relative bg-kaleido-gray-100/50 border-t border-kaleido-gray-100/80 
              px-4 py-3 flex items-center animate-fadeIn
            ">
              {/* PDF Card / Shimmer */}
              <div className="relative group shrink-0 aspect-square w-20 max-w-[20%]">
                {isUploading ? (
                  // Shimmer Loading State
                  <div className="
                    w-full h-full rounded-lg border border-gray-200 
                    bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 
                    bg-[length:200%_100%] animate-shimmer shadow-sm
                  "/>
                ) : (
                  // PDF Document Visual
                  <div className="
                    w-full h-full rounded-lg border border-gray-200 bg-white shadow-sm 
                    relative overflow-hidden cursor-default group-hover:border-kaleido-purple/40 transition-colors
                  ">
                    {/* Thumbnail Image or Fallback */}
                    {pdfThumbnail ? (
                      <img 
                        src={pdfThumbnail} 
                        alt="Document Thumbnail" 
                        className="w-full h-full object-cover opacity-90"
                      />
                    ) : (
                      /* CSS Art Fallback */
                      <div className="absolute inset-2 flex flex-col gap-1.5 opacity-40">
                        <div className="w-full h-1.5 bg-gray-800 rounded-sm mb-1" />
                        <div className="w-full h-px bg-gray-400" />
                        <div className="w-full h-px bg-gray-400" />
                        <div className="w-2/3 h-px bg-gray-400" />
                        <div className="w-full h-px bg-gray-400 mt-1" />
                        <div className="w-full h-px bg-gray-400" />
                        <div className="w-full h-px bg-gray-400" />
                        <div className="w-3/4 h-px bg-gray-400" />
                      </div>
                    )}
                    
                    {/* PDF Overlay Label */}
                    <div className="
                      absolute bottom-0 inset-x-0 
                      bg-red-600/90 backdrop-blur-[1px]
                      text-white text-[8px] font-bold tracking-wider
                      flex items-center justify-center py-0.5
                    ">
                      PDF
                    </div>

                    {/* Remove Button (Overlay) */}
                    <button 
                      onClick={removeUploadedFile}
                      className="
                        absolute top-0 right-0 p-1 
                        text-white bg-black/50 hover:bg-red-500/80
                        rounded-bl-lg transition-all opacity-0 group-hover:opacity-100
                      "
                      title="Remove file"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Chatbot;